import moment from 'logic/moment';

/**
 * Normalise the day in a personnummer date segment (…MMDD): coordination
 * numbers (samordningsnummer) encode the birth day as day + 60, so 900161 is
 * really 1990-01-01.
 *
 * @param {string} dateDigits YYMMDD or YYYYMMDD
 * @returns {string} the same string with the day de-offset
 */
const normaliseCoordinationDay = (dateDigits) => {
  const day = parseInt(dateDigits.slice(-2), 10);
  const normalisedDay = day > 60 ? day - 60 : day;

  return `${dateDigits.slice(0, -2)}${String(normalisedDay).padStart(2, '0')}`;
};

/**
 * The calendar date (YYYY-MM-DD) a birthday value names, independent of any
 * moment zone. Birthdays are stored either date-only or at UTC midnight, so an
 * ISO string names its date literally and is read as-is; other shapes (a picker
 * moment, a Date) still go through moment.
 *
 * @param {string|Date|object} birthday
 * @returns {string|null} YYYY-MM-DD, or null when not a usable date
 */
export const birthdayCalendarDate = (birthday) => {
  if (!birthday) {
    return null;
  }

  // A string birthday is always ISO here (date-only, or an ISO timestamp at UTC
  // midnight), so take the date it names verbatim rather than re-reading the
  // instant through a zone. The whole string is validated, so a malformed
  // suffix is rejected instead of being truncated to its date prefix.
  if (typeof birthday === 'string') {
    if (!moment(birthday, ['YYYY-MM-DD', moment.ISO_8601], true).isValid()) {
      return null;
    }

    const datePart = birthday.slice(0, 10);

    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
  }

  // A picked moment already carries the calendar date the user chose, so read
  // it as-is: converting to UTC would move a local midnight back a day.
  if (moment.isMoment(birthday)) {
    return birthday.isValid() ? birthday.format('YYYY-MM-DD') : null;
  }

  // A Date is a bare instant, and birthdays are stored at UTC midnight, so read
  // its UTC calendar date to match how the string form is read above.
  const parsed = moment.utc(birthday);

  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

/**
 * Whether a Swedish personnummer's embedded date (YYMMDD) matches a birthday.
 * Accepts the 10-digit (YYMMDDNNNN) or 12-digit (YYYYMMDDXXXX) form.
 *
 * Mirrors the authoritative check in the api repo (src/logic/personnummer.js);
 * the API is the source of truth, this is for instant form feedback.
 *
 * @param {string} tin
 * @param {string|Date} birthday
 * @returns {boolean}
 */
export const personnummerMatchesBirthday = (tin, birthday) => {
  if (!tin || !birthday) {
    return false;
  }

  const digits = String(tin).replace(/\D/g, '');

  // Only a full personnummer is comparable: 10 (YYMMDDNNNN) or 12
  // (YYYYMMDDXXXX) digits. Anything else is not a Swedish personnummer.
  if (digits.length !== 10 && digits.length !== 12) {
    return false;
  }

  const yymmdd = digits.length === 12 ? digits.substring(2, 8) : digits.substring(0, 6);

  const birthdayDate = birthdayCalendarDate(birthday);

  if (!birthdayDate) {
    return false;
  }

  // YYYY-MM-DD -> YYMMDD, without re-reading the date through a zone.
  return normaliseCoordinationDay(yymmdd) === birthdayDate.slice(2).replace(/-/g, '');
};
